import { ChevronRight } from 'lucide-react'
import { type ComponentPropsWithoutRef, type ReactNode } from 'react'

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '#app/components/ui/collapsible.tsx'
import { cn } from '#app/utils/misc.tsx'

type CollapsibleProps = ComponentPropsWithoutRef<typeof Collapsible>

interface AccordionProps extends Omit<CollapsibleProps, 'children'> {
	triggerContent: ReactNode
	meta?: ReactNode
	children: ReactNode
	disabled?: boolean
	containerClassName?: string
	triggerClassName?: string
	metaClassName?: string
	contentClassName?: string
	contentInnerClassName?: string
	chevronAlign?: 'start' | 'center'
	chevronPosition?: 'left' | 'right'
}

export function Accordion({
	triggerContent,
	meta,
	children,
	disabled,
	containerClassName,
	triggerClassName,
	metaClassName,
	contentClassName,
	contentInnerClassName,
	chevronAlign = 'center',
	chevronPosition = 'right',
	className: rootClassName,
	...collapsibleProps
}: AccordionProps) {
	const metaAlignment =
		chevronAlign === 'center'
			? 'self-center items-center'
			: 'self-start items-start'

	return (
		<Collapsible
			{...collapsibleProps}
			className={cn('group/accordion', rootClassName)}
		>
			<CollapsibleTrigger
				disabled={disabled}
				className={cn(
					'group focus-visible:ring-ring focus-visible:ring-offset-background inline-flex w-full flex-col text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
					disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
					triggerClassName,
				)}
			>
				<div
					className={cn(
						'bg-background group-hover:bg-muted/50 cursor-pointer rounded-lg border transition-colors',
						containerClassName,
					)}
				>
					<div className="flex items-start gap-3 p-4">
						{chevronPosition === 'left' ? (
							<div className="text-muted-foreground flex h-5 items-center pt-0.5">
								<ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-90" />
							</div>
						) : null}
						<div className="flex-1">{triggerContent}</div>
						<div
							className={cn(
								'text-muted-foreground flex gap-2',
								metaAlignment,
								metaClassName,
							)}
						>
							{meta}
							{chevronPosition === 'right' ? (
								<ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-90" />
							) : null}
						</div>
					</div>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent
				className={cn(
					'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
					contentClassName,
				)}
			>
				<div
					className={cn(
						'ml-6 space-y-3 border-l-2 border-neutral-100 py-4 pl-4',
						contentInnerClassName,
					)}
				>
					{children}
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
